import { Widget } from './widget.js';
import { WidgetException } from '../errors/widgetexception.js';
import IWidget from './iwidget.js';
import { WidgetToggler } from './widgetToggler.js';

export type WidgetValidationError = {
    widget: string,
    valid: boolean,
    lang: string,
    errors: string[]
}

/**
 * WidgetIndex holds the widgets for a form.
 * It is responsible for validating, getting values and toggling the display state via WidgetToggler.
 */
export class WidgetIndex {
    public widgets: Array<IWidget> = [];
    public toggler: WidgetToggler;

    constructor() {
        this.toggler = new WidgetToggler(this);
    }

    addWidget(widget: IWidget): void {
        this.widgets.push(widget);

        // listen to widget changes
        widget.listen('change', async () => {
            await WidgetIndex.validateSingle(widget);
        });
    }

    clear(): void {
        this.widgets = [];
    }

    /**
     * gets an array of objects: `[{name: widget_name, value: widget_value },...]`
     */
    async getValues(widgets: Array<IWidget>): Promise<Map<string, any>> {
        const promises = widgets.map(async w => w.getValue()
            .then(x => new Map([
                ['name', w.getStorageName()],
                ['value', x],
            ])));

        try {
            const result = await Promise.all(promises);
            const values = new Map();

            result.forEach((x: Map<string, any>) => {
                values.set(x.get('name'), x.get('value'));
            });

            return values;
        } catch (e) {
            console.log(e);
            return Promise.reject(new Error('couldn\'t get all values: '));
        }
    }

    /**
     * validates a single widget
     */
    static async validateSingle(w: IWidget): Promise<void> {
        await w.validate();
    }

    /**
     * validates all widgets
     */
    async validate(): Promise<boolean> {
        const formTabs = document.querySelectorAll('.tabs li.form-tab');
        const classesToRemove = ['is-danger', 'is-warning', 'is-info', 'is-success'];

        const errors: WidgetException[] = [];
        const validations = this.widgets.map(async w => w.validate());
        const validationResults = await Promise.allSettled(validations);

        // unmark form tabs
        formTabs.forEach(t => t.classList.remove(...classesToRemove));

        validationResults.forEach(v => {
            if (v.status !== 'fulfilled') {
                const error: WidgetException = v.reason;
                errors.push(error);

                // mark form tab (e.g. FR or "general")
                if (error.widget.lang !== '') {
                    formTabs.forEach(t => {
                        if ((t as HTMLElement).dataset.lang === error.widget.lang) {
                            t.classList.add(`is-${error.severity}`);
                        }
                    })
                } else if (formTabs.length > 0) { // mark general tab
                    formTabs[0].classList.add(`is-${error.severity}`);
                }
            }
        });

        console.log('validation failures:', errors);
        return errors.length === 0;
    }

    /**
     * runs the preupload task on all widgets
     */
    async runPreUploadTasks(): Promise<boolean> {
        const promises = this.widgets.map(async w => w.preUploadTask());

        const failures = (await Promise.all(promises)).filter(x => x === false);
        console.log('preupload failures:', failures);
        return failures.length === 0;
    }

    /**
     * find a widget with name and lang
     * @param name - name of field
     * @param lang - language of field
     * @param fallback - will fall back to another language if not found with supplied language
     */
    findWidget(name: string, lang?: string, fallback = false): IWidget|false {
        // try to find with supplied lang
        let foundWidget = this.widgets.find(w => w.name === name && w.lang === lang) ?? false;
        // try to find with other lang
        if (foundWidget === false && fallback === true) {
            foundWidget = this.widgets.find(w => w.name === name) ?? false;
        }
        return foundWidget;
    }

    /**
     * use the WidgetToggler to observe widgets (to toggle visibility)
     */
    observe(): void {
        this.widgets.forEach(w => this.toggler.observe(w));
        this.toggler.process();
    }
}
